import React, { createContext, useContext, useState, useEffect } from 'react';

export interface Cookie {
    id: number;
    key: string;
    name: string;
    description: string;
    category: string;
    content: string;
    retention: string;
}

export interface CookieConsent {
    [key: string]: boolean; // cookie.key => accepted/rejected
}

interface CookieConsentContextType {
    cookies: Cookie[];
    consent: CookieConsent;
    loading: boolean;
    showConsent: boolean;
    setShowConsent: (show: boolean) => void;
    acceptCookie: (key: string) => void;
    rejectCookie: (key: string) => void;
    acceptAll: () => void;
    rejectAll: () => void;
    updateConsent: (key: string, accepted: boolean) => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cookies, setCookies] = useState<Cookie[]>([]);
    const [consent, setConsent] = useState<CookieConsent>({});
    const [loading, setLoading] = useState(true);
    const [showConsent, setShowConsent] = useState(false);
    const [lastShownDate, setLastShownDate] = useState<string | null>(null);

    // Lekérdezzük a cookie-kat az API-ból
    useEffect(() => {
        const fetchCookies = async () => {
            try {
                const response = await fetch('/api/cookies');
                if (response.ok) {
                    const data = await response.json();
                    setCookies(data);

                    // Betöltjük a mentett beleegyezéseket
                    const savedConsent = localStorage.getItem('cookieConsent');
                    const savedDate = localStorage.getItem('cookieConsentDate');
                    const today = new Date().toDateString();

                    if (savedConsent) {
                        const parsed = JSON.parse(savedConsent);
                        setConsent(parsed);

                        // Ellenőrizzük, hogy volt-e már ma megjelenítve
                        if (savedDate === today) {
                            setShowConsent(false);
                        } else {
                            // Új nap - mutassuk meg az ablakot, ha vannak el nem fogadott cookiek
                            const hasUnapproved = data.some((c: Cookie) => !(c.key in parsed) || !parsed[c.key]);
                            setShowConsent(hasUnapproved);
                        }
                    } else {
                        // Első alkalommal - mutassuk meg az ablakot
                        setShowConsent(true);
                        setConsent({});
                    }

                    setLastShownDate(today);
                }
            } catch (error) {
                console.error('Error fetching cookies:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCookies();
    }, []);

    const updateConsentState = (newConsent: CookieConsent) => {
        setConsent(newConsent);
        localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
        localStorage.setItem('cookieConsentDate', new Date().toDateString());
    };

    const acceptCookie = (key: string) => {
        const newConsent = { ...consent, [key]: true };
        updateConsentState(newConsent);
    };

    const rejectCookie = (key: string) => {
        const newConsent = { ...consent };
        delete newConsent[key];
        updateConsentState(newConsent);
    };

    const acceptAll = () => {
        const newConsent: CookieConsent = {};
        cookies.forEach((c) => {
            newConsent[c.key] = true;
        });
        updateConsentState(newConsent);
        setShowConsent(false);
    };

    const rejectAll = () => {
        updateConsentState({});
        setShowConsent(false);
    };

    const updateConsent = (key: string, accepted: boolean) => {
        if (accepted) {
            acceptCookie(key);
        } else {
            rejectCookie(key);
        }
    };

    return (
        <CookieConsentContext.Provider
            value={{
                cookies,
                consent,
                loading,
                showConsent,
                setShowConsent,
                acceptCookie,
                rejectCookie,
                acceptAll,
                rejectAll,
                updateConsent,
            }}
        >
            {children}
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext);
    if (context === undefined) {
        throw new Error('useCookieConsent must be used within CookieConsentProvider');
    }
    return context;
};
